import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { apiFieldErrors } from "../../lib/companyValidation";
import {
  MAX_INTERNAL_NOTE,
  MAX_ORGANIZATION_NAME,
  platformErrorMessage,
  validateOrganizationName,
} from "../../lib/platformValidation";
import CompanyField from "../company/CompanyField";

const draftFrom = (snapshot) => ({
  name: snapshot?.organization?.name || "",
  internal_note: snapshot?.platform_note || "",
});

/**
 * The organization identity a platform administrator owns: its name and the
 * platform-only note.
 *
 * The note deliberately lives in its own table
 * (``organization_platform_metadata``), not on ``public.organizations``, because
 * ``authenticated`` can read every column of that table through PostgREST. It is
 * also NOT the license note from Phase 2D.1B, which is about the LICENSE.
 *
 * Seats stay on the license form: one editable surface per value.
 */
export const PlatformOrgProfileForm = ({ snapshot, organizationId, onSnapshot }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => draftFrom(snapshot));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const baseline = useMemo(() => draftFrom(snapshot), [snapshot]);
  useEffect(() => setDraft(baseline), [baseline]);

  const changed = useMemo(() => {
    const out = {};
    if (draft.name.trim() !== baseline.name) out.name = draft.name.trim();
    if (draft.internal_note.trim() !== baseline.internal_note) {
      out.internal_note = draft.internal_note.trim() || null;
    }
    return out;
  }, [baseline, draft]);

  const dirty = Object.keys(changed).length > 0;

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setNotice("");
  };

  const save = async (event) => {
    event.preventDefault();
    if (inFlight.current || busy) return;
    setFormError("");
    setNotice("");
    const nameError = validateOrganizationName(draft.name);
    const noteTooLong = draft.internal_note.trim().length > MAX_INTERNAL_NOTE;
    if (nameError || noteTooLong) {
      setErrors({
        name: nameError || undefined,
        internal_note: noteTooLong ? t("platform.internal_note_too_long") : undefined,
      });
      return;
    }
    setErrors({});
    if (!dirty) {
      setNotice(t("platform.no_changes"));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      const r = await api.patch(
        `/platform-admin/organizations/${organizationId}`,
        changed,
        { skipOrgHeader: true },
      );
      onSnapshot(r.data);
      setNotice(r.data?.changed ? t("platform.saved") : t("platform.no_changes"));
    } catch (err) {
      reportError("platform.updateOrganization", err);
      const fields = apiFieldErrors(err);
      if (fields) setErrors(fields);
      else setFormError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} noValidate data-testid="platform-org-form">
      <div className="qw-card">
        <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("platform.organization_section")}</div>
        <CompanyField
          id="platform-org-name"
          testId="platform-org-name-input"
          label={t("platform.org_name")}
          value={draft.name}
          onChange={(v) => setField("name", v)}
          error={errors.name}
          help={t("platform.org_name_help")}
          disabled={busy}
          maxLength={MAX_ORGANIZATION_NAME}
          required
        />
        <CompanyField
          id="platform-org-note"
          testId="platform-org-note"
          label={t("platform.platform_note")}
          value={draft.internal_note}
          onChange={(v) => setField("internal_note", v)}
          error={errors.internal_note}
          help={t("platform.platform_note_help")}
          disabled={busy}
          multiline
          rows={3}
          maxLength={MAX_INTERNAL_NOTE}
        />

        {formError && (
          <div className="qw-error" data-testid="platform-org-form-error">
            {formError}
          </div>
        )}
        {notice && (
          <div
            className="qw-help"
            style={{ color: "#059669" }}
            data-testid="platform-org-form-saved"
          >
            {notice}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button
            type="button"
            className="qw-btn ghost"
            onClick={() => {
              setDraft(baseline);
              setErrors({});
              setNotice("");
              setFormError("");
            }}
            disabled={busy || !dirty}
            data-testid="platform-org-form-cancel"
          >
            {t("platform.cancel")}
          </button>
          <button
            type="submit"
            className="qw-btn primary"
            disabled={busy}
            data-testid="platform-org-form-save"
          >
            {busy ? t("platform.saving") : t("platform.save_organization")}
          </button>
        </div>
      </div>
    </form>
  );
};

export default PlatformOrgProfileForm;
